// Meta (Facebook/Instagram) Ads panel — sibling to AdvertisingPanel
// (dashboardPanels.tsx), never a variant of it. A client running Meta but
// not Google Ads (e.g. Tyler McBroom / trm.cpa) must see his own real
// numbers here, captioned unambiguously as Meta, never inheriting Google's
// "Advertising" caption. Task #13 wires this into a two-up "Paid Media" row
// next to AdvertisingPanel — this file is the self-contained card, same
// contract every other frame in this directory follows.
//
// Structure mirrors AdvertisingPanel 1:1 on purpose (bare NotConnected on
// empty, SectionPanel-wrapped KPI strip + spend trend on data) so the two
// panels read as a matched pair when placed side by side.

import type { MetaAdsSection } from "@/types/clientAnalytics";
import { SectionPanel } from "@/components/dashboard/SectionPanel";
import {
  NotConnected,
  SourceChip,
  StatBlock,
  TrendSparkline,
  TrendChartCard,
  ChangePctBadge,
  formatNum,
  formatMoney,
  formatPct,
  formatCompactNum,
} from "@/components/client-analytics/primitives";
import { AdEntityBreakdownTable } from "@/components/client-analytics/frames/AdEntityBreakdownTable";
import { EntityClicksBarChart } from "@/components/client-analytics/frames/EntityClicksBarChart";

export function MetaAdsPanel({
  data,
  periodEnd,
}: {
  data?: MetaAdsSection | null;
  // Dashboard's period_end (see ClientAnalyticsDashboard) -- lets the spend
  // trend detect a still-in-progress trailing month and drop it instead of
  // plotting a fabricated cliff. See primitives.tsx trailingBucketIsPartial().
  periodEnd?: string | null;
}) {
  if (!data) {
    return (
      <NotConnected
        message="Meta Ads — Facebook/Instagram advertising isn't running for this account"
        cta="Talk to your account manager"
      />
    );
  }

  const hasReach = data.reach != null && data.reach > 0;
  const hasFrequency = data.frequency != null && data.frequency > 0;
  const resultsLabel = data.primary_result_label
    ? `Results (${data.primary_result_label})`
    : "Conversions";
  // ROAS from Meta = conversion value / spend, same caveat as Google Ads:
  // it only means something when the account assigns dollar values to
  // results. Most lead-gen clients don't, so a near-zero ROAS reads as
  // failure when it just means "value not tracked" — same guarded
  // treatment as AdvertisingPanel, not a fabricated 0.0x.
  const hasConv = (data.conversions ?? 0) > 0;
  const valueTracked = (data.roas ?? 0) >= 0.5;

  return (
    <SectionPanel
      title="Meta Ads"
      caption="Last 90 days · Meta Ads"
      action={<SourceChip label="Meta Ads" />}
    >
      <div className="p-5 flex flex-col gap-5">
        {/* Clicks trend (real daily series, each point's optional prior_value
            powering a paler "Previous Period" overlay -- never fabricated,
            see TrendChartCard's doc comment) paired with the by-campaign
            clicks bar chart, matching the AA "Meta Ads | Campaigns"
            reference report's own top row. Row shows whenever EITHER card
            has something real to plot; each degrades independently to its
            own "not enough data" state (a client whose facebook-ads pull
            predates clicks_over_time being wired up still gets a real
            campaigns bar chart while the trend card shows its placeholder). */}
        {(data.clicks_over_time.length >= 2 || data.campaigns.length > 0) && (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
            <div className="flex-1 min-w-0">
              <TrendChartCard
                title="Clicks trend"
                value={formatNum(data.clicks)}
                badge={<ChangePctBadge pct={data.comparison?.clicks_change_pct} />}
                seriesName="Clicks"
                points={data.clicks_over_time}
              />
            </div>
            <EntityClicksBarChart
              title="Clicks by campaign"
              rows={data.campaigns}
              searchPlaceholder="Search campaigns…"
              emptyMessage="No campaign click data yet."
            />
          </div>
        )}
        {/* flex-wrap: same KPI-row-clipping defect confirmed live on
            AdvertisingPanel's identical 7-tile row (dashboardPanels.tsx,
            "Ad spend" StatBlock) -- StatBlock's default min-width:auto
            already protects each tile's own content from shrinking below
            readable width, so a `nowrap` flex-1 row of 7 tiles (up to 8 with
            CPM/CTR both present, wider still) routinely exceeds its
            container at realistic viewport widths and silently overflows
            the page's overflow-x-hidden main column with no scrollbar and
            no way to reach the clipped tiles. Wrap onto a second line
            instead -- same fix, same reasoning, see that row's own comment
            for the full mechanism. */}
        <div className="flex flex-wrap rounded-lg border border-border bg-card divide-x divide-border">
          <StatBlock
            label="Ad spend"
            value={formatMoney(data.cost, data.currency)}
            context="this period"
            hero
            badge={<ChangePctBadge pct={data.comparison?.cost_change_pct} metric="cost" />}
            sparkline={data.cost_over_time}
            periodEnd={periodEnd}
          />
          {hasReach && (
            <StatBlock
              label="Reach"
              value={formatNum(data.reach)}
              context={hasFrequency ? `×${data.frequency!.toFixed(1)} frequency` : "people reached"}
            />
          )}
          <StatBlock
            label="Impressions"
            value={formatCompactNum(data.impressions)}
            context={!hasReach && hasFrequency ? `×${data.frequency!.toFixed(1)} frequency` : undefined}
            badge={<ChangePctBadge pct={data.comparison?.impressions_change_pct} />}
          />
          <StatBlock
            label="Clicks"
            value={formatNum(data.clicks)}
            badge={<ChangePctBadge pct={data.comparison?.clicks_change_pct} />}
          />
          {data.avg_cpc != null && (
            <StatBlock
              label="Avg. CPC"
              // 2 decimals -- Meta CPC is routinely sub-dollar ("$0.97"); the
              // default whole-dollar rounding used elsewhere in this strip
              // would collapse it to "$1" and destroy the number.
              value={formatMoney(data.avg_cpc, data.currency, 2)}
              badge={<ChangePctBadge pct={data.comparison?.avg_cpc_change_pct} metric="avg_cpc" />}
            />
          )}
          {data.ctr != null && (
            <StatBlock
              label="CTR"
              // 2 decimals -- Meta CTR is routinely sub-1% ("0.42%"); whole-
              // percent rounding would collapse it to "0%".
              value={formatPct(data.ctr, 2)}
              badge={<ChangePctBadge pct={data.comparison?.ctr_change_pct} />}
            />
          )}
          {data.cpm != null && (
            <StatBlock
              label="CPM"
              // 2 decimals -- same defect as the pre-fix Avg. CPC/CTR tiles
              // above: CPM is routinely single/double-digit dollars with a
              // meaningful cents component (ALIBI: $4.48), and the shared
              // formatMoney() 0-decimal default rounds that to "$4" -- a
              // wrong number that looks plausible, not a display nicety.
              // Confirmed against the live snapshot, not assumed: none of
              // the 13 Meta clients' cpm values are whole dollars.
              value={formatMoney(data.cpm, data.currency, 2)}
              context="per 1,000 impr."
            />
          )}
          <StatBlock
            label={resultsLabel}
            value={formatNum(data.conversions)}
            context={data.cost_per_conv != null ? `${formatMoney(data.cost_per_conv, data.currency)} / result` : undefined}
          />
          {(() => {
            if (valueTracked) {
              return (
                <StatBlock
                  label="Return on spend"
                  value={`${data.roas!.toFixed(1)}x`}
                  context="value per $ spent"
                />
              );
            }
            return (
              <StatBlock
                label="Return on spend"
                value={hasConv ? "n/a" : "—"}
                context={hasConv ? "conversion value not tracked" : "no conversion value"}
              />
            );
          })()}
        </div>
        <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
              Spend trend
            </div>
            {/* facebook-ads now uses increment="day" (Carl's ruling
                2026-08-09) -- caption flipped back from the honest "monthly"
                interim label now that it's true again. */}
            <div className="text-dash-context text-muted-foreground">daily</div>
          </div>
          <TrendSparkline points={data.cost_over_time} height={90} periodEnd={periodEnd} />
        </div>
        {/* AA "Meta Ads | Ad Sets" reference report parity -- the ad-set-level
            sibling of the campaigns bar chart above. Placed directly ahead of
            the tabbed breakdown table it complements (that table's "Ad Sets"
            tab is the same data, see AdEntityBreakdownTable's platform-aware
            tabsFor()). data.ad_groups IS the ad-set grain for Meta -- there's
            no separate "ad set" field, Meta's ad_groups are ad sets. */}
        {data.ad_groups.length > 0 && (
          <EntityClicksBarChart
            title="Clicks by ad set"
            rows={data.ad_groups}
            searchPlaceholder="Search ad sets…"
            emptyMessage="No ad set click data yet."
          />
        )}
        {/* AA "Meta Ads | Ads" reference report parity -- the creative level,
            one below ad sets. `data.ads` is typed non-optional (matches
            campaigns/ad_groups' convention) but adset-backend's builder
            patch hasn't shipped yet as of this writing, so `?.` here is
            deliberate belt-and-suspenders against an in-flight API that
            hasn't caught up to its own type -- never crash on a still-
            landing field. Same duplicate-name entities as the reference
            screenshot (one creative running in two ad sets) are expected
            and handled -- see EntityClicksBarChart's docstring. */}
        {(data.ads?.length ?? 0) > 0 && (
          <EntityClicksBarChart
            title="Clicks by ad"
            rows={data.ads!}
            searchPlaceholder="Search ads…"
            emptyMessage="No ad click data yet."
          />
        )}
        {(data.campaigns.length > 0 || data.ad_groups.length > 0) && (
          <AdEntityBreakdownTable
            campaigns={data.campaigns}
            adGroups={data.ad_groups}
            ads={data.ads}
            adGroupsCapped={data.ad_groups_capped}
            adsCapped={data.ads_capped}
            platform="Meta Ads"
            currency={data.currency}
          />
        )}
      </div>
    </SectionPanel>
  );
}
